import { useLiveQuery } from "dexie-react-hooks";
import {
  Bell, Calendar, ChevronDown, ChevronRight, ChevronUp, Clock, Copy, CornerLeftUp, Ellipsis, Flag, Hash, Inbox, Link2, ListTree, MessageSquare, Paperclip, Repeat, Send,
  Tag, Timer, Trash2, UserRound, X,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { colorHex, dueFromDateTime, toISODate, type Attachment, type Command, type Comment, type Task } from "@todoer/shared";
import { db, useCollaborators, useMemberships, useUser } from "../lib/db.js";
import { fmtDuration, relativeTime, shortDate } from "../lib/format.js";
import { start as startFocus } from "../lib/focus.js";
import { visibleTaskIds } from "../lib/selection.js";
import { cmd, dispatch, uuid } from "../lib/sync.js";
import { showToast } from "../lib/toast.js";
import { describeDue, DuePicker } from "./DuePicker.js";
import { Dialog, MenuList, OptionList, Popover, Portal, useLayer, type MenuItem } from "./overlay.js";
import { AssigneeList, DurationList, LabelList, PriorityFlag, priorityMeta, PriorityList, ProjectList, reminderText, TaskReminders } from "./pickers.js";
import { deleteTask, duplicateTask, openTaskParams, TaskCheckbox, TaskRow, TaskTree, toggleTask } from "./TaskList.js";
import { Avatar, Button, cx, IconButton, Markdown, PropertyRow, PropertyValue, Spinner, useIsPhone } from "./ui.js";

type UpdateArgs = Omit<Extract<Command, { type: "item_update" }>["args"], "id">;
type Prop = "project" | "date" | "deadline" | "priority" | "labels" | "assignee" | "reminders" | "duration" | "parent" | "more";

/** Ids of task rows in the page behind the panel, for J/K. */
const listIds = () => visibleTaskIds().filter((id, i, all) => all.indexOf(id) === i);

/** Full task editor: a side panel on desktop, a full-height sheet on phone. */
export function TaskDetail({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const task = useLiveQuery(async () => (await db.tasks.get(taskId)) ?? null, [taskId], "loading" as const);
  const loaded: Task | null = task === "loading" ? null : task;
  const isPhone = useIsPhone();
  const [, setParams] = useSearchParams();

  const step = useCallback((delta: number) => {
    const ids = listIds();
    const i = ids.indexOf(taskId);
    const next = ids[i < 0 ? 0 : i + delta];
    if (next && next !== taskId) setParams(openTaskParams(next));
  }, [taskId, setParams]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest("input, textarea, [contenteditable=true]") || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "j" || e.key === "J") { e.preventDefault(); step(1); }
      if (e.key === "k" || e.key === "K") { e.preventDefault(); step(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  if (task === "loading") return null;
  const body = loaded ? <TaskEditor key={loaded.id} task={loaded} onClose={onClose} step={step} isPhone={isPhone} /> : (
    <div className="p-6">
      <p className="text-body">This task no longer exists. It may have been deleted or moved to a project you can't see.</p>
      <Button variant="primary" className="mt-4" onClick={onClose}>Close</Button>
    </div>
  );
  if (isPhone) return <Dialog title="Task details" onClose={onClose} phone="full" bodyClassName="flex min-h-0 flex-1 flex-col" header={<span />}>{body}</Dialog>;
  return <SidePanel onClose={onClose}>{body}</SidePanel>;
}

function SidePanel({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayer("panel", () => ref.current, onClose);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    return () => { if (prev?.isConnected && !prev.closest("[data-task-panel]")) prev.focus({ preventScroll: true }); };
  }, []);
  return (
    <Portal>
      <aside ref={ref} role="dialog" aria-modal="false" aria-label="Task details" data-task-panel
        className="fixed inset-y-0 right-0 z-40 flex w-[480px] max-w-full flex-col border-l border-border bg-surface-raised shadow-modal anim-panel">
        {children}
      </aside>
    </Portal>
  );
}

function TaskEditor({ task: t, onClose, step, isPhone }: { task: Task; onClose: () => void; step: (d: number) => void; isPhone: boolean }) {
  const projects = useLiveQuery(() => db.projects.toArray(), [], []);
  const sections = useLiveQuery(() => db.sections.where("projectId").equals(t.projectId).sortBy("sectionOrder"), [t.projectId], []);
  const labels = useLiveQuery(() => db.labels.toArray(), [], []);
  const projectTasks = useLiveQuery(() => db.tasks.where("projectId").equals(t.projectId).toArray(), [t.projectId], []);
  const parent = useLiveQuery(() => (t.parentId ? db.tasks.get(t.parentId) : undefined), [t.parentId]);
  const reminders = useLiveQuery(() => db.reminders.where("taskId").equals(t.id).toArray(), [t.id], []);
  const comments = useLiveQuery(() => db.comments.where("taskId").equals(t.id).sortBy("createdAt"), [t.id], []);
  const people = useCollaborators();
  const memberships = useMemberships();
  const me = useUser();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [content, setContent] = useState(t.content);
  const [description, setDescription] = useState(t.description);
  const [editingDesc, setEditingDesc] = useState(false);
  const [pop, setPop] = useState<{ kind: Prop; anchor: HTMLElement } | null>(null);
  const [showDoneSubs, setShowDoneSubs] = useState(false);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (el) { el.style.height = "auto"; el.style.height = `${el.scrollHeight}px`; }
  }, [content]);
  useEffect(() => {
    if (params.get("focus") === "comment") {
      composerRef.current?.focus();
      setParams((p) => { p.delete("focus"); return p; }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = useCallback((args: UpdateArgs) => dispatch(cmd("item_update", { id: t.id, ...args })), [t.id]);
  const flushText = useCallback(() => {
    const changes: UpdateArgs = {};
    const c = content.trim();
    if (c && c !== t.content) changes.content = c;
    if (description !== t.description) changes.description = description;
    if (Object.keys(changes).length) void update(changes);
  }, [t, content, description, update]);
  const flushRef = useRef(flushText);
  flushRef.current = flushText;
  useEffect(() => () => flushRef.current(), []);

  const close = () => { flushText(); onClose(); };
  const project = projects.find((p) => p.id === t.projectId);
  const section = sections.find((s) => s.id === t.sectionId);
  const members = useMemo(() => {
    const ids = new Set(memberships.filter((m) => m.projectId === t.projectId).map((m) => m.userId));
    return people.filter((p) => ids.has(p.id));
  }, [t.projectId, memberships, people]);
  const assignee = people.find((p) => p.id === t.assigneeId);

  // Sub-tasks: every descendant, so nested ones show in the tree.
  const descendants = useMemo(() => {
    const out: Task[] = [];
    const walk = (id: string) => { for (const c of projectTasks.filter((x) => x.parentId === id)) { out.push(c); walk(c.id); } };
    walk(t.id);
    return out;
  }, [projectTasks, t.id]);
  const directSubs = descendants.filter((d) => d.parentId === t.id);
  const doneSubs = directSubs.filter((d) => d.completedAt);
  const parentOptions = useMemo(() => {
    const below = new Set([t.id, ...descendants.map((d) => d.id)]);
    return projectTasks.filter((x) => !x.completedAt && !below.has(x.id)).sort((a, b) => a.content.localeCompare(b.content));
  }, [projectTasks, descendants, t.id]);

  const openTask = (id: string) => { flushText(); setParams(openTaskParams(id)); };
  const toggleComplete = async () => {
    flushText();
    const closing = !t.completedAt && !t.due?.isRecurring;
    await toggleTask(t);
    if (closing) onClose();
  };
  const remove = async () => { await deleteTask(t); onClose(); };
  const move = (args: { projectId?: string; sectionId?: string | null; parentId?: string | null }) => dispatch(cmd("item_move", { id: t.id, ...args }));
  const focusNow = () => { flushText(); startFocus(t.id); onClose(); navigate("/focus"); };
  const openPop = (kind: Prop) => (e: { currentTarget: HTMLElement }) => setPop({ kind, anchor: e.currentTarget });
  const closePop = () => setPop(null);
  const goProject = () => { flushText(); navigate(project?.isInbox ? "/inbox" : `/project/${t.projectId}`); };

  const moreItems: MenuItem[] = [
    { label: "Start focus session", icon: Timer, onSelect: focusNow },
    { label: "Duplicate", icon: Copy, onSelect: () => void duplicateTask(t) },
    { label: "Copy link", icon: Link2, onSelect: () => { void navigator.clipboard?.writeText(`${location.origin}${location.pathname}?task=${t.id}`); showToast("Link copied"); } },
    { label: "Delete task", icon: Trash2, danger: true, divider: true, onSelect: () => void remove() },
  ];

  const crumbs = (
    <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1 text-meta text-text-muted">
      <button type="button" onClick={goProject} className="flex min-w-0 items-center gap-1.5 rounded px-1 py-0.5 hover:bg-hover hover:text-text">
        {project?.isInbox ? <Inbox size={14} className="shrink-0 text-info" aria-hidden /> : <Hash size={14} className="shrink-0" style={{ color: colorHex(project?.color ?? "grey") }} aria-hidden />}
        <span className="truncate">{project ? (project.isInbox ? "Inbox" : project.name) : ""}</span>
      </button>
      {section && <><ChevronRight size={12} className="shrink-0 text-text-faint" aria-hidden /><button type="button" onClick={goProject} className="truncate rounded px-1 py-0.5 hover:bg-hover hover:text-text">{section.name}</button></>}
      {parent && <><ChevronRight size={12} className="shrink-0 text-text-faint" aria-hidden /><button type="button" onClick={() => openTask(parent.id)} className="truncate rounded px-1 py-0.5 hover:bg-hover hover:text-text">{parent.content}</button></>}
    </nav>
  );

  const header = (
    <header className="flex h-12 shrink-0 items-center gap-1 border-b border-border px-3 phone:h-14">
      {isPhone && <IconButton icon={ChevronDown} label="Close" onClick={close} />}
      {crumbs}
      {!isPhone && <>
        <IconButton icon={ChevronUp} label="Previous task" shortcut="K" onClick={() => step(-1)} />
        <IconButton icon={ChevronDown} label="Next task" shortcut="J" onClick={() => step(1)} />
        <IconButton icon={Timer} label="Start focus session" onClick={focusNow} />
      </>}
      <IconButton icon={Ellipsis} label="More actions" onClick={openPop("more")} active={pop?.kind === "more"} />
      {!isPhone && <IconButton icon={X} label="Close" shortcut="Esc" onClick={close} />}
    </header>
  );

  const due = t.due;
  const pm = priorityMeta(t.priority);
  const taskLabels = labels.filter((l) => t.labelIds.includes(l.id));

  const body = (
    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-6 pt-4 phone:px-4">
      <div className="flex items-start gap-3">
        <div className="pt-1"><TaskCheckbox priority={t.priority} done={!!t.completedAt} onToggle={() => void toggleComplete()} size={22} /></div>
        <textarea ref={titleRef} rows={1} value={content} onChange={(e) => setContent(e.target.value.replace(/\n/g, " "))} onBlur={flushText}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLTextAreaElement).blur(); } }} aria-label="Task name"
          className={cx("block w-full resize-none overflow-hidden bg-transparent text-subtitle font-semibold outline-none", t.completedAt && "text-text-faint line-through")} />
      </div>

      {t.completedAt && (
        <div className="mt-3 flex items-center gap-2 rounded-card bg-success-soft px-3 py-2 text-body text-success">
          <span className="flex-1">Completed {relativeTime(t.completedAt)}</span>
          <button type="button" onClick={() => void toggleComplete()} className="rounded-control px-2 py-1 font-medium hover:bg-success/10">Reopen</button>
        </div>
      )}

      <div className="mt-4 space-y-0.5" role="group" aria-label="Properties">
        <PropertyRow icon={project?.isInbox ? Inbox : Hash} label="Project">
          <PropertyValue onClick={openPop("project")} aria-label={`Project: ${project?.isInbox ? "Inbox" : project?.name ?? ""}`}>
            {project?.isInbox ? <Inbox size={14} className="text-info" aria-hidden /> : <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colorHex(project?.color ?? "grey") }} />}
            <span className="truncate">{project?.isInbox ? "Inbox" : project?.name}{section && <span className="text-text-muted"> › {section.name}</span>}</span>
          </PropertyValue>
        </PropertyRow>
        <PropertyRow icon={due?.isRecurring ? Repeat : Calendar} label="Date">
          <PropertyValue empty={!due} onClick={openPop("date")} aria-label={due ? `Date: ${describeDue(due)}` : "Set date"}>
            {due && <span className={cx(due.date < toISODate(new Date()) ? "text-danger" : "")}>{describeDue(due)}{due.isRecurring && <span className="text-text-muted"> · next {shortDate(due.date)}</span>}</span>}
          </PropertyValue>
        </PropertyRow>
        <PropertyRow icon={Flag} label="Deadline">
          <PropertyValue empty={!t.deadline} onClick={openPop("deadline")} aria-label={t.deadline ? `Deadline: ${shortDate(t.deadline)}` : "Set deadline"}>
            {t.deadline && new Date(`${t.deadline}T00:00`).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short", year: "numeric" })}
          </PropertyValue>
        </PropertyRow>
        <PropertyRow icon={Flag} label="Priority">
          <PropertyValue onClick={openPop("priority")} aria-label={`Priority: ${pm.label}`}><PriorityFlag priority={t.priority} />{pm.label}</PropertyValue>
        </PropertyRow>
        <PropertyRow icon={Tag} label="Labels">
          <PropertyValue empty={!taskLabels.length} onClick={openPop("labels")} aria-label={taskLabels.length ? `Labels: ${taskLabels.map((l) => l.name).join(", ")}` : "Add labels"}>
            <span className="flex flex-wrap gap-1">{taskLabels.map((l) => <span key={l.id} className="rounded-full bg-surface-sunken px-2 py-0.5 text-meta text-text-muted">@{l.name}</span>)}</span>
          </PropertyValue>
        </PropertyRow>
        {members.length > 1 && (
          <PropertyRow icon={UserRound} label="Assignee">
            <PropertyValue empty={!assignee} onClick={openPop("assignee")} aria-label={assignee ? `Assignee: ${assignee.name}` : "Assign"}>
              {assignee && <><Avatar person={assignee} size={20} />{assignee.name}</>}
            </PropertyValue>
          </PropertyRow>
        )}
        <PropertyRow icon={Bell} label="Reminders">
          <PropertyValue empty={!reminders.length} onClick={openPop("reminders")} aria-label="Reminders">
            {reminders.length > 0 && <span className="truncate">{reminders.map((r) => reminderText(r)).join(", ")}</span>}
          </PropertyValue>
        </PropertyRow>
        <PropertyRow icon={Clock} label="Duration">
          <PropertyValue empty={!t.durationMinutes} onClick={openPop("duration")} aria-label={t.durationMinutes ? `Duration: ${fmtDuration(t.durationMinutes)}` : "Set duration"}>
            {t.durationMinutes && fmtDuration(t.durationMinutes)}
          </PropertyValue>
        </PropertyRow>
        <PropertyRow icon={CornerLeftUp} label="Parent">
          <PropertyValue empty={!parent} emptyText="None" onClick={openPop("parent")} aria-label={parent ? `Parent task: ${parent.content}` : "Set parent task"}>
            {parent && <span className="truncate">{parent.content}</span>}
          </PropertyValue>
        </PropertyRow>
      </div>

      <section className="mt-6" aria-labelledby="desc-h">
        <h3 id="desc-h" className="mb-1.5 text-caption font-semibold uppercase text-text-faint">Description</h3>
        {editingDesc ? (
          <textarea autoFocus value={description} onChange={(e) => setDescription(e.target.value)} onBlur={() => { flushText(); setEditingDesc(false); }}
            placeholder="Notes, links, checklists… **bold**, - lists and links work" aria-label="Description" rows={Math.max(4, description.split("\n").length + 1)}
            className="block w-full resize-y rounded-control border border-border bg-surface px-3 py-2 text-body outline-none focus:border-brand focus:ring-2 focus:ring-brand/20" />
        ) : (
          <button type="button" onClick={() => setEditingDesc(true)} aria-label="Edit description"
            className="-mx-2 block w-[calc(100%+16px)] rounded-control px-2 py-1.5 text-left text-body hover:bg-hover">
            {description ? <Markdown text={description} /> : <span className="text-text-faint">Add a description…</span>}
          </button>
        )}
      </section>

      <section className="mt-6" aria-labelledby="subs-h">
        <div className="mb-1 flex items-center gap-2">
          <h3 id="subs-h" className="text-caption font-semibold uppercase text-text-faint">Sub-tasks</h3>
          {directSubs.length > 0 && <span className="tabular text-meta text-text-faint">{doneSubs.length}/{directSubs.length}</span>}
        </div>
        {directSubs.length > 0 && (
          <div className="mb-2 h-1 overflow-hidden rounded-full bg-surface-sunken" role="progressbar" aria-label="Sub-tasks done" aria-valuemin={0} aria-valuemax={directSubs.length} aria-valuenow={doneSubs.length}>
            <div className="h-full rounded-full bg-success transition-[width] duration-300" style={{ width: `${(doneSubs.length / directSubs.length) * 100}%` }} />
          </div>
        )}
        <TaskTree tasks={descendants.filter((d) => !d.completedAt)} allTasks={descendants} projectId={t.projectId} sectionId={t.sectionId} parentId={t.id} dense />
        {doneSubs.length > 0 && (
          <>
            <button type="button" onClick={() => setShowDoneSubs((s) => !s)} aria-expanded={showDoneSubs} className="mt-1 flex items-center gap-1 text-meta text-text-muted hover:text-text">
              {showDoneSubs ? <ChevronDown size={14} aria-hidden /> : <ChevronRight size={14} aria-hidden />}{doneSubs.length} completed
            </button>
            {showDoneSubs && <ul className="mt-1">{doneSubs.map((s) => <TaskRow key={s.id} task={s} dense hideOnComplete={false} />)}</ul>}
          </>
        )}
      </section>

      <CommentList comments={comments} meId={me?.id} people={people} />
      {!isPhone && <Composer taskId={t.id} inputRef={composerRef} />}
    </div>
  );

  const footer = isPhone && (
    <div className="shrink-0 border-t border-border bg-surface-raised pb-safe">
      <Composer taskId={t.id} inputRef={composerRef} compact />
      <div className="flex items-center gap-2 px-3 pb-2">
        <Button variant={t.completedAt ? "secondary" : "primary"} className="flex-1" onClick={() => void toggleComplete()}>{t.completedAt ? "Reopen" : "Complete"}</Button>
        <Button className="flex-1" icon={Calendar} onClick={openPop("date")}>Schedule</Button>
        <IconButton icon={Ellipsis} label="More actions" onClick={openPop("more")} />
      </div>
    </div>
  );

  return (
    <>
      {header}
      {body}
      {footer}
      {pop?.kind === "more" && <Popover anchor={pop.anchor} onClose={closePop} title="Task actions" align="end" width={220}><MenuList label="Task actions" items={moreItems} onClose={closePop} /></Popover>}
      {pop?.kind === "project" && (
        <Popover anchor={pop.anchor} onClose={closePop} title="Move to" width={280}>
          <ProjectList withSections value={t.projectId} sectionValue={t.sectionId} onSelect={(projectId, sectionId) => {
            closePop();
            if (!projectId) return;
            if (projectId !== t.projectId) void move({ projectId, sectionId, parentId: null });
            else if (sectionId !== t.sectionId) void move({ sectionId, parentId: null });
          }} />
        </Popover>
      )}
      {pop?.kind === "date" && <Popover anchor={pop.anchor} onClose={closePop} title="Date" width={300}><DuePicker value={t.due} onChange={(d) => void update({ due: d })} onPicked={closePop} /></Popover>}
      {pop?.kind === "deadline" && (
        <Popover anchor={pop.anchor} onClose={closePop} title="Deadline" width={300}>
          <DuePicker dateOnly value={t.deadline ? dueFromDateTime(t.deadline, null) : null} onChange={(d) => void update({ deadline: d?.date ?? null })} onPicked={closePop} />
        </Popover>
      )}
      {pop?.kind === "priority" && <Popover anchor={pop.anchor} onClose={closePop} title="Priority" width={220}><PriorityList value={t.priority} onSelect={(p) => { closePop(); void update({ priority: p }); }} /></Popover>}
      {pop?.kind === "labels" && <Popover anchor={pop.anchor} onClose={closePop} title="Labels" width={260}><LabelList selected={t.labelIds} onChange={(ids) => void update({ labelIds: ids })} /></Popover>}
      {pop?.kind === "assignee" && <Popover anchor={pop.anchor} onClose={closePop} title="Assignee" width={260}><AssigneeList members={members} value={t.assigneeId} meId={me?.id} onSelect={(id) => { closePop(); void update({ assigneeId: id }); }} /></Popover>}
      {pop?.kind === "reminders" && <Popover anchor={pop.anchor} onClose={closePop} title="Reminders" width={300}><TaskReminders task={t} /></Popover>}
      {pop?.kind === "duration" && <Popover anchor={pop.anchor} onClose={closePop} title="Duration" width={200}><DurationList value={t.durationMinutes} onSelect={(m) => { closePop(); void update({ durationMinutes: m }); }} /></Popover>}
      {pop?.kind === "parent" && (
        <Popover anchor={pop.anchor} onClose={closePop} title="Parent task" width={300}>
          <OptionList label="Parent task" search placeholder="Find a task" options={[
            { key: "none", label: "None (top level)", selected: !t.parentId, onSelect: () => { closePop(); void move({ parentId: null }); } },
            ...parentOptions.map((p) => ({ key: p.id, label: p.content, icon: ListTree, selected: p.id === t.parentId, onSelect: () => { closePop(); void move({ parentId: p.id }); } })),
          ]} />
        </Popover>
      )}
    </>
  );
}

function CommentList({ comments, meId, people }: { comments: Comment[]; meId: string | undefined; people: { id: string; name: string; image: string | null }[] }) {
  return (
    <section className="mt-6" aria-labelledby="comments-h">
      <div className="mb-2 flex items-center gap-2">
        <h3 id="comments-h" className="text-caption font-semibold uppercase text-text-faint">Comments</h3>
        {comments.length > 0 && <span className="tabular text-meta text-text-faint">{comments.length}</span>}
      </div>
      {!comments.length && <p className="flex items-center gap-2 text-body text-text-faint"><MessageSquare size={16} aria-hidden />No comments yet.</p>}
      <ul className="space-y-4">
        {comments.map((c) => {
          const who = people.find((p) => p.id === c.userId);
          const isImage = c.attachment?.mime.startsWith("image/");
          return (
            <li key={c.id} className="group/comment flex gap-3">
              <Avatar person={who ?? { name: "?", image: null }} size={28} />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-body font-semibold">{who?.name ?? "Someone"}</span>
                  <time dateTime={c.createdAt} title={new Date(c.createdAt).toLocaleString()} className="text-meta text-text-faint">{relativeTime(c.createdAt)}</time>
                  {c.userId === meId && (
                    <IconButton size="sm" icon={Trash2} label="Delete comment" tone="danger" className="ml-auto opacity-0 group-hover/comment:opacity-100 focus-visible:opacity-100 phone:opacity-100"
                      onClick={() => void dispatch(cmd("note_delete", { id: c.id }))} />
                  )}
                </div>
                {c.content && <Markdown text={c.content} className="mt-0.5 text-body" />}
                {c.attachment && (
                  isImage
                    ? <a href={`/api/files/${c.attachment.fileId}`} target="_blank" rel="noreferrer" className="mt-2 block w-fit overflow-hidden rounded-card border border-border">
                        <img src={`/api/files/${c.attachment.fileId}`} alt={c.attachment.name} className="max-h-48 max-w-full object-cover" />
                      </a>
                    : <a href={`/api/files/${c.attachment.fileId}`} target="_blank" rel="noreferrer" className="mt-2 flex w-fit max-w-full items-center gap-3 rounded-card border border-border bg-surface px-3 py-2 hover:border-border-strong">
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-control bg-surface-sunken text-text-muted"><Paperclip size={16} aria-hidden /></span>
                        <span className="min-w-0"><span className="block truncate text-body">{c.attachment.name}</span><span className="text-meta text-text-faint">{Math.max(1, Math.round(c.attachment.size / 1024))} KB</span></span>
                      </a>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Composer({ taskId, inputRef, compact }: { taskId: string; inputRef: React.RefObject<HTMLTextAreaElement | null>; compact?: boolean }) {
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const post = async (attachment: Attachment | null = null) => {
    if (!text.trim() && !attachment) return;
    await dispatch(cmd("note_add", { id: uuid(), taskId, content: text.trim(), attachment }));
    setText("");
  };
  const upload = async (file: File) => {
    setUploading(true); setError(null);
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await fetch("/api/files", { method: "POST", body, credentials: "include" });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message ?? "Upload failed");
      await post(json as Attachment);
    } catch (e) { setError(e instanceof Error ? e.message : "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div className={cx(compact ? "flex items-end gap-1 px-3 py-2" : "mt-4 rounded-card border border-border bg-surface focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20")}>
      <textarea ref={inputRef} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment" aria-label="Write a comment" rows={compact ? 1 : 2}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void post(); } }}
        className={cx("block w-full resize-none bg-transparent text-body outline-none", compact ? "min-h-11 rounded-[22px] bg-surface-sunken px-4 py-3" : "px-3 pt-2.5")} />
      <input ref={fileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
      {compact ? (
        <>
          {uploading ? <span className="grid h-11 w-11 place-items-center"><Spinner /></span> : <IconButton icon={Paperclip} label="Attach a file" onClick={() => fileRef.current?.click()} />}
          <IconButton icon={Send} label="Post comment" tone="brand" onClick={() => void post()} disabled={!text.trim()} />
        </>
      ) : (
        <div className="flex items-center gap-2 px-2 pb-2">
          <IconButton size="sm" icon={Paperclip} label="Attach a file" onClick={() => fileRef.current?.click()} disabled={uploading} />
          {uploading && <Spinner />}
          {error && <span className="text-meta text-danger">{error}</span>}
          <Button size="sm" variant="primary" className="ml-auto" onClick={() => void post()} disabled={!text.trim()} shortcut="Ctrl Enter">Comment</Button>
        </div>
      )}
      {compact && error && <span className="text-meta text-danger">{error}</span>}
    </div>
  );
}
